// APIWorkflows.js
import { 
    Box, Grid, GridItem, Text, VStack, HStack, Link, Divider, Heading, 
    Code, Container, Button, Spinner, Badge, Tabs, TabList, TabPanels, 
    Tab, TabPanel, useColorModeValue, UnorderedList, ListItem, Table,
    Thead, Tbody, Tr, Th, Td, Accordion, AccordionItem, AccordionButton,
    AccordionPanel, AccordionIcon, OrderedList, Icon, Flex, Alert, AlertIcon,
    Card, CardHeader, CardBody, CardFooter, Stack, StackDivider
  } from "@chakra-ui/react";
  import { ExternalLinkIcon, InfoIcon, CheckCircleIcon, ArrowForwardIcon } from '@chakra-ui/icons';
  import HeaderMenus from './HeaderMenus';
  import React, { useState, useContext } from 'react';
  import { AppContext } from '../components/AppContext';
  
  function APIWorkflows() {
    const bgColor = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const codeBg = useColorModeValue("gray.50", "gray.700");
    const accentColor = useColorModeValue("blue.500", "blue.300");
    const cardBg = useColorModeValue("white", "gray.700");
    
    // Custom Step component for workflows
    const WorkflowStep = ({ title, description, endpoint, parameters, optional = false }) => {
      return (
        <Box mb={6} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor} position="relative">
          {optional && (
            <Badge position="absolute" top={2} right={2} colorScheme="purple">
              Optional
            </Badge>
          )}
          <Heading as="h4" size="md" mb={2} color={accentColor}>
            {title}
          </Heading>
          <Text mb={3}>{description}</Text>
          <Box mb={3}>
            <HStack>
              <Badge colorScheme="blue">Endpoint</Badge>
              <Code>{endpoint}</Code>
            </HStack>
          </Box>
          {parameters && (
            <Box>
              <Text fontWeight="bold" mb={2}>Key Parameters:</Text>
              <Box pl={4}>
                {parameters.map((param, index) => (
                  <HStack key={index} mb={1} alignItems="flex-start">
                    <Code>{param.name}</Code>
                    <Text>- {param.description}</Text>
                  </HStack>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      );
    };
  
    // Custom component for response handling
    const ResponseSection = ({ title, items }) => {
      return (
        <Box mb={6} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor} bg={codeBg}>
          <Heading as="h4" size="md" mb={3}>
            {title}
          </Heading>
          <UnorderedList spacing={2}>
            {items.map((item, index) => (
              <ListItem key={index}>{item}</ListItem>
            ))}
          </UnorderedList>
        </Box>
      );
    };
  
    // Workflow card component with standard steps
    const WorkflowCard = ({ title, description, steps, children }) => {
      return (
        <Card mb={8} variant="outline" bg={cardBg}>
          <CardHeader pb={0}>
            <Heading size="md">{title}</Heading>
            <Text pt={2}>{description}</Text>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" mb={6} spacing={0}>
              {steps.map((step, index) => (
                <HStack key={index} spacing={4} p={2}>
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg={accentColor}
                    color="white"
                    justify="center"
                    align="center"
                    fontWeight="bold"
                  >
                    {index + 1}
                  </Flex>
                  <Box>
                    <Text fontWeight="bold">{step.title}</Text>
                    <Text fontSize="sm" color="gray.500">{step.description}</Text>
                  </Box>
                </HStack>
              ))}
            </VStack>
            {children}
          </CardBody>
        </Card>
      );
    };
  
    return (
      <Box>
        <Grid
          templateAreas={`"header header"    
                          "nav main"
                          "footer footer"`}
          gridTemplateRows={'60px 1fr 60px'}
          gridTemplateColumns={'300px 1fr'}
          h='100vh'
          gap='2'
          color='blackAlpha.800'
          fontWeight='bold'
        >
          {/* Header with menu */}
          <HeaderMenus />
  
          {/* Navigation sidebar */}
          <GridItem area="nav" bg={bgColor} p={4} overflowY="auto" borderRight="1px" borderColor={borderColor}>
            <VStack spacing={4} align="start" width="100%">
              <Heading as="h3" size="md" mb={2}>API Integration Workflows</Heading>
              
              <VStack align="start" spacing={2} width="100%">
                <Link href="#overview" fontSize="md" fontWeight="semibold">Overview</Link>
                
                <Heading as="h4" size="sm" mt={2}>Integration Paths</Heading>
                <Link href="#path1" fontSize="sm" pl={4}>Path 1: Standard E/M with Audio</Link>
                <Link href="#path2" fontSize="sm" pl={4}>Path 2: Audio with Separate Coding</Link>
                <Link href="#path3" fontSize="sm" pl={4}>Path 3: Existing Documentation</Link>
                
                <Heading as="h4" size="sm" mt={2}>Additional Resources</Heading>
                <Link href="#capabilities" fontSize="sm" pl={4}>Additional Capabilities</Link>
                <Link href="/api-docs" fontSize="sm" pl={4}>
                  API Reference <ExternalLinkIcon mx="2px" />
                </Link>
              </VStack>
            </VStack>
          </GridItem>
  
          {/* Main content area */}
          <GridItem area="main" bg={bgColor} p={6} overflowY="auto">
            <Box maxW="1000px" mx="auto">
              {/* Overview Section */}
              <Box id="overview" mb={10}>
                <Heading as="h1" size="xl" mb={4}>API Integration Workflows</Heading>
                <Text fontSize="lg" mb={4}>
                  This guide outlines three distinct implementation paths for integrating with Revedy's API based on different clinical documentation workflows. Each path leverages existing Revedy API endpoints to achieve the end-to-end workflow from documentation to coding and medical necessity analysis.
                </Text>
                
                <Alert status="info" variant="left-accent" mb={6}>
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Before You Begin</Text>
                    <Text>Make sure you have your API credentials ready. All API calls require authentication with a valid access token.</Text>
                  </Box>
                </Alert>
                
              </Box>
  
              {/* Path 1 Section */}
              <Box id="path1" mb={12} pt={4}>
                <Flex align="center" mb={4}>
                  <Badge colorScheme="green" fontSize="md" mr={3}>Path 1</Badge>
                  <Heading as="h2" size="lg">Standard E/M with Audio Input</Heading>
                </Flex>
                
                <Text fontSize="md" mb={6}>
                  This workflow is ideal when a provider conducts a standard E/M visit, captures audio, and needs full documentation, coding, and medical necessity analysis in a streamlined process.
                </Text>
                
                <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                  <Heading as="h3" size="md" mb={4}>Workflow Overview</Heading>
                  <Flex direction="column">
                    {[
                      { number: 1, title: "Authentication", description: "Obtain access token" },
                      { number: 2, title: "Process Audio", description: "Generate note with coding" },
                      { number: 3, title: "Medical Necessity", description: "Analyze coverage requirements" }
                    ].map((step, idx) => (
                      <React.Fragment key={idx}>
                        <Flex align="center" mb={idx === 2 ? 0 : 3}>
                          <Flex
                            w="40px"
                            h="40px"
                            borderRadius="full"
                            bg={accentColor}
                            color="white"
                            justify="center"
                            align="center"
                            fontWeight="bold"
                            mr={4}
                          >
                            {step.number}
                          </Flex>
                          <Box>
                            <Text fontWeight="bold">{step.title}</Text>
                            <Text fontSize="sm" color="gray.500">{step.description}</Text>
                          </Box>
                        </Flex>
                        {idx < 2 && (
                          <Box pl="20px" ml="20px" borderLeft="2px" borderColor={borderColor} h="20px" />
                        )}
                      </React.Fragment>
                    ))}
                  </Flex>
                </Box>
                
                <WorkflowStep 
                  title="Step 1: Authenticate and Get Access Token"
                  description="Obtain an access token for API authentication."
                  endpoint="POST /oauth/token"
                  parameters={[
                    { name: "client_id", description: "Your client ID" },
                    { name: "client_secret", description: "Your client secret" },
                    { name: "audience", description: "https://revedy.io/demo" },
                    { name: "grant_type", description: "client_credentials" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 2: Process Audio and Generate Note with Coding"
                  description="Upload audio recording and generate a clinical note with automatic coding."
                  endpoint="POST /v2/encounter-analysis"
                  parameters={[
                    { name: "encounter_id", description: "Unique identifier for the encounter in your system" },
                    { name: "external_patient_id", description: "Patient identifier" },
                    { name: "external_provider_id", description: "Provider identifier" },
                    { name: "analysis_type", description: "Set to \"coding\" to include CPT and ICD-10 coding" },
                    { name: "note_template_id", description: "Specify template ID based on specialty needs" },
                    { name: "audio", description: "The audio file(s) of the encounter" },
                    { name: "additional_data", description: "Array of additional clinical data points (optional)" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 3: Retrieve Medical Necessity Analysis"
                  description="Analyze the medical necessity of the coded procedures for specific payers."
                  endpoint="POST /medical_necessity_analysis"
                  parameters={[
                    { name: "encounter_id", description: "Use the same encounter id you provided in step 2" },
                    { name: "payer", description: "Specify payer (e.g., \"cms\" for Medicare, \"aetna\" for Aetna)" },
                    { name: "override_state", description: "Set to true to use contractor with best coverage" }
                  ]}
                />
                
                <ResponseSection 
                  title="Response Handling"
                  items={[
                    "The /v2/encounter-analysis response includes: Generated clinical note, CPT codes with justifications, ICD-10 codes with evidence and justifications, and External case ID for subsequent API calls.",
                    "The /medical_necessity_analysis response includes: Medical necessity score for each CPT code, Payer-specific coverage analysis, Supporting documentation requirements, and Recommendations for improving medical necessity."
                  ]}
                />
              </Box>
  
              {/* Path 2 Section */}
              <Box id="path2" mb={12} pt={4}>
                <Flex align="center" mb={4}>
                  <Badge colorScheme="blue" fontSize="md" mr={3}>Path 2</Badge>
                  <Heading as="h2" size="lg">General Solution with Audio Input (Separate Coding)</Heading>
                </Flex>
                
                <Text fontSize="md" mb={6}>
                  This workflow is designed for providers who capture audio but want more control over the coding process or need to add additional documentation before coding.
                </Text>
                
                <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                  <Heading as="h3" size="md" mb={4}>Workflow Overview</Heading>
                  <Flex direction="column">
                    {[
                      { number: 1, title: "Authentication", description: "Obtain access token" },
                      { number: 2, title: "Process Audio", description: "Generate note without coding" },
                      { number: 3, title: "Upload Content", description: "Add supplementary documentation (optional)" },
                      { number: 4, title: "CPT Coding", description: "Generate procedure codes" },
                      { number: 5, title: "ICD-10 Coding", description: "Generate diagnosis codes" },
                      { number: 6, title: "Medical Necessity", description: "Analyze coverage requirements" }
                    ].map((step, idx) => (
                      <React.Fragment key={idx}>
                        <Flex align="center" mb={idx === 5 ? 0 : 3}>
                          <Flex
                            w="40px"
                            h="40px"
                            borderRadius="full"
                            bg={accentColor}
                            color="white"
                            justify="center"
                            align="center"
                            fontWeight="bold"
                            mr={4}
                          >
                            {step.number}
                          </Flex>
                          <Box>
                            <Text fontWeight="bold">{step.title}</Text>
                            <Text fontSize="sm" color="gray.500">{step.description}</Text>
                          </Box>
                        </Flex>
                        {idx < 5 && (
                          <Box pl="20px" ml="20px" borderLeft="2px" borderColor={borderColor} h="20px" />
                        )}
                      </React.Fragment>
                    ))}
                  </Flex>
                </Box>
                
                <WorkflowStep 
                  title="Step 1: Authenticate and Get Access Token"
                  description="Obtain an access token for API authentication."
                  endpoint="POST /oauth/token"
                  parameters={[
                    { name: "client_id", description: "Your client ID" },
                    { name: "client_secret", description: "Your client secret" },
                    { name: "audience", description: "https://revedy.io/demo" },
                    { name: "grant_type", description: "client_credentials" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 2: Process Audio and Generate Note Without Coding"
                  description="Upload audio recording and generate a clinical note without automatic coding."
                  endpoint="POST /v2/encounter-analysis"
                  parameters={[
                    { name: "encounter_id", description: "Unique identifier for the encounter" },
                    { name: "external_patient_id", description: "Patient identifier" },
                    { name: "external_provider_id", description: "Provider identifier" },
                    { name: "note_template_id", description: "Specify template ID based on specialty needs" },
                    { name: "audio", description: "The audio file(s) of the encounter" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 3: Upload Additional Content"
                  description="Upload additional text content to supplement the note."
                  endpoint="POST /upload_content/{case_id}"
                  parameters={[
                    { name: "case_id", description: "The external_case_id returned from encounter analysis" },
                    { name: "file", description: "The text content to upload" },
                    { name: "file_type_id", description: "Specify file type (e.g., 5 for Clinical Notes)" }
                  ]}
                  optional={true}
                />
                
                <WorkflowStep 
                  title="Alternative Step 3: Upload Additional Files"
                  description="Upload additional files (PDFs, images, etc.)."
                  endpoint="POST /upload_file/{case_id}"
                  parameters={[
                    { name: "case_id", description: "The external_case_id returned from encounter analysis" },
                    { name: "file", description: "The file to upload" },
                    { name: "file_type_id", description: "Specify file type" }
                  ]}
                  optional={true}
                />
                
                <WorkflowStep 
                  title="Step 4: Perform CPT Coding"
                  description="Generate CPT codes based on the clinical documentation."
                  endpoint="POST /cpt_coding"
                  parameters={[
                    { name: "case_id", description: "The external case ID from encounter analysis" },
                    { name: "case_type", description: "Specify the type of case (e.g., \"Surgery\", \"E/M\")" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 5: Perform ICD-10 Coding"
                  description="Generate ICD-10 diagnosis codes based on the clinical documentation."
                  endpoint="POST /icd10_coding"
                  parameters={[
                    { name: "case_id", description: "The external case ID from encounter analysis" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 6: Retrieve Medical Necessity Analysis"
                  description="Analyze the medical necessity of the coded procedures for specific payers."
                  endpoint="POST /medical_necessity_analysis"
                  parameters={[
                    { name: "case_id", description: "The external case ID" },
                    { name: "payer", description: "Specify payer (e.g., \"cms\", \"aetna\")" },
                    { name: "override_state", description: "Set to true to use contractor with best coverage" }
                  ]}
                />
                
                <ResponseSection 
                  title="Response Handling"
                  items={[
                    "Check the status of coding processes by retrieving case details using GET /get_case_details/{case_id}",
                    "The case details response includes the status of CPT and ICD-10 coding processes"
                  ]}
                />
              </Box>
  
              {/* Path 3 Section */}
              <Box id="path3" mb={12} pt={4}>
                <Flex align="center" mb={4}>
                  <Badge colorScheme="purple" fontSize="md" mr={3}>Path 3</Badge>
                  <Heading as="h2" size="lg">General Solution with Existing Documentation (No Audio)</Heading>
                </Flex>
                
                <Text fontSize="md" mb={6}>
                  This workflow is designed for providers who already have documentation and only need coding and medical necessity analysis.
                </Text>
                
                <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
                  <Heading as="h3" size="md" mb={4}>Workflow Overview</Heading>
                  <Flex direction="column">
                    {[
                      { number: 1, title: "Authentication", description: "Obtain access token" },
                      { number: 2, title: "Create Case", description: "Create a new case for coding" },
                      { number: 3, title: "Upload Documentation", description: "Add existing clinical documentation" },
                      { number: 4, title: "CPT Coding", description: "Generate procedure codes" },
                      { number: 5, title: "ICD-10 Coding", description: "Generate diagnosis codes" },
                      { number: 6, title: "Medical Necessity", description: "Analyze coverage requirements" }
                    ].map((step, idx) => (
                      <React.Fragment key={idx}>
                        <Flex align="center" mb={idx === 5 ? 0 : 3}>
                          <Flex
                            w="40px"
                            h="40px"
                            borderRadius="full"
                            bg={accentColor}
                            color="white"
                            justify="center"
                            align="center"
                            fontWeight="bold"
                            mr={4}
                          >
                            {step.number}
                          </Flex>
                          <Box>
                            <Text fontWeight="bold">{step.title}</Text>
                            <Text fontSize="sm" color="gray.500">{step.description}</Text>
                          </Box>
                        </Flex>
                        {idx < 5 && (
                          <Box pl="20px" ml="20px" borderLeft="2px" borderColor={borderColor} h="20px" />
                        )}
                      </React.Fragment>
                    ))}
                  </Flex>
                </Box>
                
                <WorkflowStep 
                  title="Step 1: Authenticate and Get Access Token"
                  description="Obtain an access token for API authentication."
                  endpoint="POST /oauth/token"
                  parameters={[
                    { name: "client_id", description: "Your client ID" },
                    { name: "client_secret", description: "Your client secret" },
                    { name: "audience", description: "https://revedy.io/demo" },
                    { name: "grant_type", description: "client_credentials" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 2: Create a New Case"
                  description="Create a new case for coding."
                  endpoint="POST /create_external_case"
                  parameters={[
                    { name: "external_case_id", description: "Unique identifier for the case in your system" },
                    { name: "case_type", description: "Specify the type of case (e.g., \"Surgery\", \"E/M\")" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 3: Upload Existing Documentation"
                  description="Upload text content for the case."
                  endpoint="POST /upload_content/{case_id}"
                  parameters={[
                    { name: "case_id", description: "The case ID returned from create_external_case" },
                    { name: "file", description: "The text content to upload" },
                    { name: "file_type_id", description: "Specify file type (e.g., 5 for Clinical Notes)" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Alternative Step 3: Upload Files"
                  description="Upload files (PDFs, images, etc.)."
                  endpoint="POST /upload_file/{case_id}"
                  parameters={[
                    { name: "case_id", description: "The case ID returned from create_external_case" },
                    { name: "file", description: "The file to upload" },
                    { name: "file_type_id", description: "Specify file type" }
                  ]}
                  optional={true}
                />
                
                <WorkflowStep 
                  title="Step 4: Perform CPT Coding"
                  description="Generate CPT codes based on the clinical documentation."
                  endpoint="POST /cpt_coding"
                  parameters={[
                    { name: "case_id", description: "The case ID returned from create_external_case" },
                    { name: "case_type", description: "Specify the type of case" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 5: Perform ICD-10 Coding"
                  description="Generate ICD-10 diagnosis codes based on the clinical documentation."
                  endpoint="POST /icd10_coding"
                  parameters={[
                    { name: "case_id", description: "The case ID" }
                  ]}
                />
                
                <WorkflowStep 
                  title="Step 6: Retrieve Medical Necessity Analysis"
                  description="Analyze the medical necessity of the coded procedures for specific payers."
                  endpoint="POST /medical_necessity_analysis"
                  parameters={[
                    { name: "encounter_id", description: "The encounter ID from your system" },
                    { name: "payer", description: "Specify payer" },
                    { name: "override_state", description: "Set to true to use contractor with best coverage" }
                  ]}
                />
                
                <ResponseSection 
                  title="Response Handling"
                  items={[
                    "Monitor the status of coding processes by retrieving case details using GET /get_case_details/{case_id}"
                  ]}
                />
              </Box>
  
              {/* Additional Capabilities Section */}
              <Box id="capabilities" mb={10} pt={4}>
                <Heading as="h2" size="lg" mb={6}>Additional Capabilities</Heading>
                
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                  <Box p={5} shadow="md" borderWidth="1px" borderRadius="lg">
                    <Heading as="h3" size="md" mb={4}>Private Label UI</Heading>
                    <Text>
                      Revedy offers a private label UI that can be used for reviewing and providing feedback on AI-generated notes and codes. This can be integrated into your platform if desired.
                    </Text>
                  </Box>
                  <Box p={5} shadow="md" borderWidth="1px" borderRadius="lg">
                  <Heading as="h3" size="md" mb={4}>Additional API Endpoints</Heading>
                  <Text mb={3}>
                    Beyond the core workflows outlined above, Revedy offers additional capabilities:
                  </Text>
                  <UnorderedList spacing={1}>
                    <ListItem>Note editing and updating</ListItem>
                    <ListItem>Patient-facing content generation</ListItem>
                    <ListItem>Case overview generation</ListItem>
                    <ListItem>File download and management</ListItem>
                  </UnorderedList>
                </Box>
              </Grid>
              
              <Box mt={8} p={5} shadow="md" borderWidth="1px" borderRadius="lg">
                <Heading as="h3" size="md" mb={4}>Next Steps</Heading>
                <Text mb={4}>
                  For more detailed information about specific API endpoints, please refer to our comprehensive API documentation.
                </Text>
                <Button as={Link} href="/api-docs" colorScheme="blue">
                  View API Reference <ExternalLinkIcon ml={2} />
                </Button>
              </Box>
            </Box>
          </Box>
        </GridItem>

        {/* Footer */}
        <GridItem area="footer" bg={bgColor} borderTop="1px" borderColor={borderColor} p={4} textAlign="center">
          <Text fontSize="sm">© {new Date().getFullYear()} Revedy. All rights reserved.</Text>
        </GridItem>
      </Grid>
    </Box>
  );
}

export default APIWorkflows;